import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALL_CONNECTION_QUERY_KEY,
  TELEPHONE_MUTATION_KEY,
} from 'constants/QueryKeys.constants';
import { useApi } from 'contexts/ApiContext';

import { upCaseFirstLetter } from '@polyai/common/lib/stringHelper';

import { curryPostFunction } from 'api/utils';
import { PROJECT_RESOURCE, useProjectResource } from './useProjectResources';
import useProjectRoute from './useProjectRoute';
import { useToast } from './useToast';

const useTelephony = (enabled: boolean = true) => {
  const { accountId, projectId } = useProjectRoute();

  const api = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const queryKey = [accountId, projectId, TELEPHONE_MUTATION_KEY];
  const allConnectionTokensQueryKey = [
    accountId,
    projectId,
    TELEPHONE_MUTATION_KEY,
    ALL_CONNECTION_QUERY_KEY,
  ];
  const { isProjectResourceEnabled } = useProjectResource();
  const isEnabled =
    enabled &&
    (isProjectResourceEnabled(PROJECT_RESOURCE.SETTINGS) ||
      isProjectResourceEnabled(PROJECT_RESOURCE.HANDOFFS));

  const { data, isLoading: isLoadingConnectionToken } = useQuery(
    queryKey,
    () => api.methods.getConnectionToken(accountId, projectId),
    {
      enabled: isEnabled,
      retry: false,
    },
  );

  const { data: allConnectionTokens, isLoading: isLoadingAllConnectionTokens } =
    useQuery(
      allConnectionTokensQueryKey,
      () => api.methods.getAllConnectionTokens(accountId, projectId),
      {
        enabled: isEnabled,
        retry: false,
      },
    );

  const {
    mutate: createTelephonyConnector,
    isLoading: isCreatingTelephonyConnector,
  } = useMutation(
    curryPostFunction(api.methods.createConnector)({ accountId, projectId }),
    {
      retry: false,
      mutationKey: queryKey,
      onSuccess: (_, { clientEnv }) => {
        queryClient
          .invalidateQueries(queryKey)
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
        queryClient
          .invalidateQueries(allConnectionTokensQueryKey)
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
        toast.success({
          title: `${upCaseFirstLetter(clientEnv)} SIP address generated`,
        });
      },
    },
  );

  return useMemo(
    () => ({
      connectionToken: data?.token,
      isLoadingConnectionToken: isEnabled && isLoadingConnectionToken,
      createTelephonyConnector,
      isCreatingTelephonyConnector,
      allConnectionTokens,
      isLoadingAllConnectionTokens,
    }),
    [
      data?.token,
      isEnabled,
      isLoadingConnectionToken,
      createTelephonyConnector,
      isCreatingTelephonyConnector,
      allConnectionTokens,
      isLoadingAllConnectionTokens,
    ],
  );
};

export default useTelephony;
